import Signal from "signals";
import { get } from "svelte/store";

import BaseModel from "./BaseModel";
import VesselGroup from "./VesselGroup";

import GeoHelper from "../Helpers/GeoHelper";
import { calcLocalTimeFromPoint } from "../Helpers/Misc/timeHelper";

import AircraftProperty from "./Enums/AircraftProperty";
import AircraftGeneralDataPropertyMap from "./Enums/AircraftGeneralDataPropertyMap";
import Path from "./Path";

import ModelType from "./ModelType";

import AircraftStore from "../Aviation/AircraftStore";
import VesselIcon from "./Enums/VesselIcon";
import VesselColour from "../Enums/VesselColour";
import AircraftIcon from "./Enums/AircraftIcon";
import AircraftPathConfig from "./AircraftPathConfig";
import IconMode from "../Map/Enums/IconMode";
import AviationAlertsStore from "../Aviation/AviationAlertsStore";

class Aircraft {
	constructor(data, group = null, map = AircraftGeneralDataPropertyMap) {
		// WARNING! this._feature should be used internally, all external use should rely on deep copy from this.feature
		// - additionally references to the properties object as a whole should be done via this.feature.properties
		// otherwise unexpected behaviour can occur
		if (!group) {
			group = new VesselGroup();
		}
		this.type = ModelType.AIRCRAFT;
		this.group = group;
		this.map = map;
		this._feature = {
			type: "Feature",
			properties: {
			},
			geometry: {
				type: "Point",
				coordinates: data.geometry.coordinates
			}
		};

		// See comment for BaseModel.remap.
		BaseModel.remap(this._feature, data, AircraftProperty, this.map);

		if (data.properties.heading) {
			this._feature.properties.course = data.properties.heading;
		}
		else {
			this._feature.properties.course = this.calculateCourse(this.state, this.destinationIATA, this.lng, this.lat);
		}

		// console.log("new Aircraft", this._feature, data);

		this.uniqidType = AircraftProperty.REGISTRATION;
		this._focused = false;
		this._selected = false;
		this._visible = true;
		this._historicPath = null;
		this.onSelectedChanged = new Signal();

		this._alerts = [];

		// this.registerAlertType(alertType, alertCategory);
		this._feature.properties.iconImage = setIconImage(this, get(AviationAlertsStore.iconMode));

		this.updateSnapshot();
	}

	generateHistoricPath(messages) {
		// Remap from Alerts/Spire properties to internal property names.
		const newMessages = messages.map((messageFeature) => {
			const obj = GeoHelper.featureAt(messageFeature.geometry.coordinates[0], messageFeature.geometry.coordinates[1]);
			BaseModel.remap(obj, messageFeature, AircraftProperty, this.map);
			return obj;
		});
		this._historicPath = new Path(this, newMessages, AircraftPathConfig);

		// Sometimes historic/messages data is more recent than 'live' data. Account for that here!
		// We edit the live data properties and coordinates with the latest from historic.
		// https://trello.com/c/6fPq91rU/284-messages-data-is-sometimes-more-recent-that-alerts-data
		// const mostRecentStop = this._historicPath.stops._stopArray[this._historicPath.stops._stopArray.length - 1];
		// if (mostRecentStop && mostRecentStop.properties[AircraftProperty.TIMESTAMP] > this._feature.properties[AircraftProperty.TIMESTAMP]) {
		// 	for (const key in mostRecentStop.properties) {
		// 		this._feature.properties[key] = mostRecentStop.properties[key];
		// 	}
		// 	this._feature.geometry.coordinates = mostRecentStop.geometry.coordinates;
		this.updateSnapshot();
		// }
	}

	getDefaultDate() {
		return get(this.group.timeSliderData.value);
	}

	// This is called by AlertsController on all vessels whenever the snapshot slider value changes,
	// afterwhich layerdata is renewed
	updateSnapshot(date = null) {
		if (date === null) {
			date = this.getDefaultDate();
		}

		this._feature.properties.iconImage = this.iconImage;

		if (!this.historicPath) {
			this._snapshotFeature = date.isBefore(this.timestamp) ? null : this.feature;
			return;
		}

		const snapshotData = this.historicPath.getSnapshotVesselFeature(date);
		if (!snapshotData) {
			// Calculation attempted but unsuccesful!
			this._snapshotFeature = null;
			return;
		}

		if (!this._snapshotFeature) this._snapshotFeature = this.feature;

		this._snapshotFeature.geometry.coordinates = snapshotData.geometry.coordinates;
		this._snapshotFeature.properties = {
			...this.feature.properties,
			[AircraftProperty.TIMESTAMP]: snapshotData.properties[AircraftProperty.TIMESTAMP],
			[AircraftProperty.STATE]: snapshotData.properties[AircraftProperty.STATE],
			[AircraftProperty.ALTITUDE]: snapshotData.properties[AircraftProperty.ALTITUDE],
			[AircraftProperty.ALTITUDE_BARO]: snapshotData.properties[AircraftProperty.ALTITUDE_BARO],
			[AircraftProperty.COURSE]: snapshotData.properties[AircraftProperty.COURSE]
			// 	? snapshotData.properties[AircraftProperty.COURSE]
			// 	: null
		};

		if (!snapshotData.properties[AircraftProperty.COURSE]) {
			this._snapshotFeature.properties.course = this.calculateCourse(this.snapshotState, this._snapshotFeature.properties[AircraftProperty.DESTINATION_IATA], this.snapshotLng, this.snapshotLat);
		}

		setIconImage(this, get(AviationAlertsStore.iconMode));
		// always dirty.
		// if (this.isDirty) {
		// this.updateSnapshot(date);
		// }
	}

	// TODO: legacy, remove, point to this.feature
	asFeature() {
		console.warn("Please change to vessel.feature rather than vessel.asFeature()");
		return this.feature;
	}

	addAlertData(alertType, alertCategory = "") {
		// TODO: this is not production ready.
		// At the moment, we colour Aircraft pink if they have "any" alerts, and white if they don't (e.g. Aircraft history).
		// Keeping it simple for GEOINT demo purposes until we know what we're doing long-term. ^AG
		this._alerts.push(alertType);
		this._feature.properties.iconImage = setIconImage(this, get(AviationAlertsStore.iconMode));
	}

	setIconMode(iconMode) {
		setIconImage(this, iconMode);
		// TODO: probably doesn't need full snapshot update?
		this.updateSnapshot();
	}

	// region Getters and Setters
	get iconImage() {
		// let img = "airport-15";
		// if (typeof this.icaoAddress !== "undefined") {
		// const icon = VesselIcon[VesselColour.BLACK];
		// img = this.selected ? icon.highlight : icon.standard;
		// }
		// console.log("iconImage", img);
		// return img;
		return this._feature.properties.iconImage;
	}

	set iconImage(iconImage) {
		// TODO: figure out why `this._feature.properties.iconImage = iconImage` doesn't work! (Copied from Vessel.js)
		this._feature = {
			...this._feature,
			properties: {
				...this._feature.properties,
				iconImage: iconImage
			}
		};
	}

	get lineColor() {
		// console.log(this.iconImage);
		return VesselIcon.helpers.toColor(this.iconImage);
	}

	// Returns deep copy, readonly
	// Never allow this to circularly feed anything directly in this._feature
	get feature() {
		return JSON.parse(JSON.stringify(this._feature));
	}

	get currentFeature() {
		if (this.hasActiveSnapshot()) {
			return this.snapshotFeature;
		}
		return this.feature;
	}

	// Returns deep copy, readonly
	get snapshotFeature() {
		if (this._snapshotFeature === undefined) {
			this.updateSnapshot();
		}
		if (this._snapshotFeature === null) {
			console.warn("No history for current snapshot date");
			return false;
		}

		const response = { ...this._snapshotFeature };
		response.geometry = { ...this._snapshotFeature.geometry };
		response.properties = { ...this._snapshotFeature.properties };
		return response;
	}

	get snapshotTimestamp() {
		return this._snapshotFeature.properties[AircraftProperty.TIMESTAMP];
	}

	// TODO: We will want to rehook the mapping from server data keys to local data keys.
	// This involves hooking back up to BaseModel, and removing AlertProp.
	get timestamp() {
		return this._feature.properties[AircraftProperty.TIMESTAMP];
	}

	get currentTimestamp() {
		return (this.hasActiveSnapshot())
			? this.snapshotTimestamp
			: this.timestamp;
	}

	get coordinates() {
		return this._feature.geometry.coordinates;
	}

	get state() {
		return this._feature.properties[AircraftProperty.STATE];
	}

	get snapshotState() {
		return this._snapshotFeature.properties[AircraftProperty.STATE];
	}

	calculateCourse(state, destinationIata, lng, lat) {
		if (state === "landed") {
			return 0;
		}
		const airport = get(AircraftStore.results.airportsByIATA)[destinationIata];
		if (!airport) {
			console.warn("cannot find UK airport for IATA code", destinationIata);
			return 0;
		}
		// console.log("destinationIata", destinationIata, "airport", airport);

		const forceMinus180Positive180Angle = (angle) => {
			if (angle < 0) {
				angle = angle % 360;
				angle += 360;
			}
			else if (angle > 0) {
				angle = angle % 360;
			}
			if (angle > 180) {
				angle -= 360;
			}
			return angle;
		};

		const anglef = (x1, y1, x2, y2) => {
			if (x1 === x2 && y1 === y2) {
				return 0;
			}
			else if (x1 === x2) {
				if (y1 > y2) {
					return 270;
				}
				else {
					return 90;
				}
			}
			else if (y1 === y2) {
				if (x1 > x2) {
					return 180;
				}
				else {
					return 0;
				}
			}

			const xDifference = x2 - x1;
			const yDifference = y2 - y1;

			const angle = (Math.atan2(yDifference, xDifference) * (180 / Math.PI));
			return forceMinus180Positive180Angle(angle);
		};

		// +90 bceause the icon points up usually.
		// *-1 because +y axis is up.
		return (anglef(lng, lat, airport.lng, airport.lat) * -1) + 90;
	}

	get course() {
		// if (this._feature.properties[AircraftProperty.STATE] === "landed") {
		// 	return 0;
		// }
		return this._feature.properties.course;
	}

	get snapshotCourse() {
		if (this._snapshotFeature) {
			return this._snapshotFeature.properties.course;
		}
		return null;
	}

	get lat() {
		return this.coordinates[1];
	}

	get lon() {
		return this.coordinates[0];
	}

	get lng() {
		return this.lon;
	}

	get snapshotLng() {
		return this.snapshotCoordinates[0];
	}

	get snapshotLat() {
		return this.snapshotCoordinates[1];
	}

	get snapshotCoordinates() {
		return this._snapshotFeature.geometry.coordinates;
	}

	get snapshotLocalTime() {
		const pointLocalTime = calcLocalTimeFromPoint(this.snapshotCoordinates);
		return (pointLocalTime != null) ? pointLocalTime : "<em>[unavailable]</em>";
	}

	get name() {
		return this.registration + " (" + this.flightNumber + ")";
	}

	get callSign() {
		return this._feature.properties[AircraftProperty.CALL_SIGN] || "N/A";
	}

	get destinationIATA() {
		return this._feature.properties[AircraftProperty.DESTINATION_IATA];
	}

	get destinationICAO() {
		return this._feature.properties[AircraftProperty.DESTINATION_ICAO];
	}

	get originIATA() {
		return this._feature.properties[AircraftProperty.ORIGIN_IATA];
	}

	get originICAO() {
		return this._feature.properties[AircraftProperty.ORIGIN_ICAO];
	}

	get altitude() {
		return this._feature.properties[AircraftProperty.ALTITUDE];
	}

	get snapshotAltitude() {
		return this._snapshotFeature.properties[AircraftProperty.ALTITUDE];
	}

	get currentAltitude() {
		if (this.hasActiveSnapshot()) {
			return this.snapshotAltitude;
		}
		return this.altitude;
	}

	get altitudeBaro() {
		return this._feature.properties[AircraftProperty.ALTITUDE_BARO];
	}

	get snapshotAltitudeBaro() {
		return this._snapshotFeature.properties[AircraftProperty.ALTITUDE_BARO];
	}

	get currentAltitudeBaro() {
		if (this.hasActiveSnapshot()) {
			return this.snapshotAltitudeBaro;
		}
		return this.altitudeBaro;
	}

	get currentValidAltitude() {
		let a = this.currentAltitudeBaro;
		if (!a) {
			a = this.currentAltitude;
		}
		return a;
	}

	get sil() {
		return this._feature.properties[AircraftProperty.SIL];
	}

	get snapshotSil() {
		return this._snapshotFeature.properties[AircraftProperty.SIL];
	}

	get nacp() {
		return this._feature.properties[AircraftProperty.NAC_P];
	}

	get snapshotNacp() {
		return this._snapshotFeature.properties[AircraftProperty.NAC_P];
	}

	get event() {
		return this._feature.properties[AircraftProperty.STATE];
	}

	get flight() {
		return this._feature.properties[AircraftProperty.FLIGHT_NUMBER];
	}

	get flightNumber() {
		return this._feature.properties[AircraftProperty.FLIGHT_NUMBER] || "N/A";
	}

	get flightOperator() {
		return this._feature.properties[AircraftProperty.FLIGHT_OPERATOR];
	}

	get operator() {
		return this._feature.properties[AircraftProperty.FLIGHT_OPERATOR];
	}

	get equipment() {
		return this._feature.properties[AircraftProperty.EQUIPMENT];
	}

	get registration() {
		return this._feature.properties[AircraftProperty.REGISTRATION] || "N/A";
	}

	get icaoAddress() {
		return this._feature.properties[AircraftProperty.ICAO_ADDRESS];
	}

	get localTime() {
		const pointLocalTime = calcLocalTimeFromPoint(this.coordinates);
		return (pointLocalTime != null) ? pointLocalTime : "<em>[unavailable]</em>";
	}

	get currentCoordinates() {
		if (this.hasActiveSnapshot()) {
			return this._snapshotFeature.geometry.coordinates;
		}
		return this.coordinates;
	}

	get selected() {
		return this._selected;
	}

	set selected(value) {
		this._selected = value;
		setIconImage(this, get(AviationAlertsStore.iconMode));
		// TODO: Should this be called by the thing that calls `isSelected` instead of being inside? Or is
		//  there a way to wait a little bit (a resetting warm-up?) on the action to allow other calls?
		// TODO: Vessels should probably belong to a 'group' i.e. alerts, vessel history, vessels nearby
		// 	query, instead of always triggering something in AlertsStore. (^AG)
		// this.addOrRemovePropertyToWritableList("imo", AlertsStore.selection.vesselImos, value);
		this.addOrRemovePropertyToWritableList(this.uniqidType, this.group.selected[this.uniqidType], value);
		this.onSelectedChanged.dispatch(value);
		// TODO: probably doesn't need full snapshot update?
		this.updateSnapshot();
	}

	set focused(value) {
		this._focused = value;
		// We set the focused state on the group.
		this.addOrRemovePropertyToWritableList(this.uniqidType, this.group.focused[this.uniqidType], value);
	}

	get focused() {
		return this._focused;
	}

	isSTS() {
		return false;
	}

	addOrRemovePropertyToWritableList(property, list, value) {
		list.update(existing => {
			if (value) {
				return [...existing, this[property]];
			}
			else {
				return existing.filter(existingData => existingData !== this[property]);
			}
		});
	}

	get visible() {
		return this._visible;
	}

	set visible(value) {
		this._visible = value;
	}

	get historicPath() {
		return this._historicPath;
	}

	set historicPath(path) {
		console.warn("Please use `generageHistoricPath` rather than setting directly");
		this._historicPath = path;
	}

	get indexInGroup() {
		const groupVessels = get(this.group.vessels);
		return Math.max(groupVessels.indexOf(this), 0);
	}

	hasActiveSnapshot() {
		return this._snapshotFeature && this._snapshotFeature.properties[AircraftProperty.TIMESTAMP] < this._feature.properties[AircraftProperty.TIMESTAMP];
	}

	// endregion (getters and setters)
} // Vessel

const setIconImage = (aircraft, iconMode) => {
	aircraft.isDirty = false;

	const prevIcon = aircraft.iconImage;
	let baseIcon = AircraftIcon.WHITE;
	if (iconMode === IconMode.AVIATION_ALERT) {
		if (aircraft._alerts.length > 0) {
			baseIcon = AircraftIcon[VesselColour.MAGENTA];
		}
		else {
			baseIcon = AircraftIcon[VesselColour.WHITE];
		}
	}
	else if (iconMode === IconMode.INDEX) {
		const keys = Object.keys(AircraftIcon);
		keys.splice(0, 1); // Remove WHITE
		keys.splice(keys.length - 1, 1); // Remove "helpers"
		baseIcon = AircraftIcon[keys[aircraft.indexInGroup % keys.length]];
		// console.log("indexInGroup", vessel.indexInGroup, "baseIcon", baseIcon);
	}
	else if (iconMode === IconMode.AVIATION_SIL) {
		if (typeof aircraft.sil === "undefined" || aircraft.sil === "") {
			baseIcon = AircraftIcon[VesselColour.WHITE];
		}
		else {
			const silIconMap = [
				VesselColour.LIGHTRED,
				VesselColour.ORANGE,
				VesselColour.YELLOW,
				VesselColour.LIGHTGREEN
			];
			const sil = parseInt(aircraft.sil);
			console.log("sil", sil, aircraft.sil, aircraft);
			baseIcon = AircraftIcon[silIconMap[sil]];
		}
	}
	else if (iconMode === IconMode.AVIATION_NACP) {
		if (typeof aircraft.nacp === "undefined" || aircraft.nacp === "") {
			baseIcon = AircraftIcon[VesselColour.WHITE];
		}
		else {
			const nacpIconMap = [
				VesselColour.LIGHTRED, 		// 0
				VesselColour.ORANGE,		// 1
				VesselColour.ORANGE,		// 2
				VesselColour.ORANGE,		// 3
				VesselColour.ORANGE,		// 4
				VesselColour.ORANGE,		// 5
				VesselColour.ORANGE,		// 6
				VesselColour.YELLOW,		// 7
				VesselColour.YELLOW,		// 8
				VesselColour.LIGHTGREEN,	// 9
				VesselColour.LIGHTGREEN,	// 10
				VesselColour.LIGHTGREEN,	// 11
				VesselColour.LIGHTGREEN		// 12 just in case
			];
			const nacp = parseInt(aircraft.nacp);
			console.log("nacp", nacp);
			baseIcon = AircraftIcon[nacpIconMap[nacp]];
		}
	}

	// TODO: We should do this so that we can continue using the "circle" options.
	// aircraftBaseIcon = vessel icon to aircraft icon func

	aircraft.iconImage = aircraft.selected ? baseIcon.highlight : baseIcon.standard;
	// if ((aircraft.hasActiveSnapshot() && aircraft.snapshotCourse == null) ||
	// 	(!aircraft.hasActiveSnapshot() && aircraft.course == null)) {
	// 	aircraft.iconImage = aircraft.selected ? baseIcon.circleHighlight : baseIcon.circleStandard;
	// }
	if (prevIcon !== aircraft.iconImage) {
		aircraft.isDirty = true;
	}
};

export default Aircraft;
